import React, { useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import { CalculationResult } from '../types';
import { Tooltip } from './Tooltip';
import { Clock, DollarSign, UserCheck, Edit2, Timer, Download } from 'lucide-react';
import { colorSystem } from '../utils/colorSystem';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

interface ResultsDisplayProps {
  result: CalculationResult;
  showChart?: boolean;
  calculatorRef?: React.RefObject<HTMLDivElement>;
  onShowTrend?: () => void;
}

export function ResultsDisplay({ result, showChart = true, calculatorRef, onShowTrend }: ResultsDisplayProps) {
  const analysisRef = useRef<HTMLDivElement>(null);

  // Reset color system for new chart instance
  React.useEffect(() => {
    colorSystem.reset();
  }, []);

  const handleEditClick = () => {
    if (calculatorRef?.current) {
      calculatorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleExport = async (format: 'pdf' | 'png') => {
    if (!analysisRef.current) return;

    try {
      // Find and temporarily hide the button section
      const buttonSection = analysisRef.current.querySelector('.export-exclude');
      const originalDisplay = buttonSection ? (buttonSection as HTMLElement).style.display : '';
      if (buttonSection) {
        (buttonSection as HTMLElement).style.display = 'none';
      }

      const canvas = await html2canvas(analysisRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true
      });

      // Restore button section visibility
      if (buttonSection) {
        (buttonSection as HTMLElement).style.display = originalDisplay;
      }

      if (format === 'png') {
        const link = document.createElement('a');
        link.download = `productivity-analysis-${new Date().toISOString().split('T')[0]}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } else {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`productivity-analysis-${new Date().toISOString().split('T')[0]}.pdf`);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatHours = (value: number) => {
    if (value >= 0) {
      return `${Math.floor(value * 100) / 100} Hours`;
    } else {
      return `${Math.ceil(value * 100) / 100} Hours`;
    }
  };

  const getRolePlural = (role: string) => {
    switch (role) {
      case 'Licensed Users - Blended':
        return 'Licensed Users - Blended';
      case 'Service Agent':
        return 'Service Agents';
      case 'Sales Development Rep':
        return 'Sales Development Reps';
      case 'Account Executive':
        return 'Account Executives';
      default:
        return role;
    }
  };

  const chartData = result.isHoursMode ? (
    result.knownExperiencesMode ? [
      {
        name: 'Time Lost to\nLoading/Year',
        value: Math.abs(result.annualLinearImpactHours || 0),
        color: colorSystem.getColor('Linear', 'Impact')
      },
      {
        name: 'Time Lost to\nProductivity/Year',
        value: Math.abs(result.annualExponentialImpactHours || 0),
        color: colorSystem.getColor('Exponential', 'Impact')
      },
      {
        name: 'Total Upside\nPotential/Year',
        value: Math.abs((result.annualLinearImpactHours || 0) + (result.annualExponentialImpactHours || 0)),
        color: colorSystem.getColor('Total', 'Upside')
      }
    ] : [
      {
        name: 'Expected\nAnnual Hours',
        value: (result.hoursPerDay || 0) * (result.workingDaysPerYear || 250),
        color: colorSystem.getColor('Expected', 'Hours')
      },
      {
        name: 'Actual\nAnnual Hours',
        value: ((result.productivity / 100) * (result.hoursPerDay || 0)) * (result.workingDaysPerYear || 250),
        color: colorSystem.getColor('Actual', 'Hours')
      },
      {
        name: 'Hours\nDifference',
        value: (result.hoursDifference || 0) * (result.workingDaysPerYear || 250),
        color: colorSystem.getColor('Difference', 'Hours')
      }
    ]
  ) : [
    {
      name: 'Average\nWage',
      value: result.cost,
      color: colorSystem.getColor('Average', 'Wage')
    },
    {
      name: 'Value\nProduced',
      value: result.valueProduced,
      color: colorSystem.getColor('Value', 'Produced')
    },
    {
      name: 'Cost/\nBenefit',
      value: result.trueCost,
      color: colorSystem.getColor('Cost', 'Benefit')
    }
  ];

  const generateSummary = () => {
    if (result.knownExperiencesMode) {
      const totalAnnualImpact = (result.annualLinearImpactHours || 0) + (result.annualExponentialImpactHours || 0);
      const loadsText = result.tasksPerDay.toLocaleString();
      const currentEPT = result.ept.toFixed(1);
      const targetEPT = result.targetEpt?.toFixed(1) || '1.0';
      const dohertyRateCurrent = result.dohertyRateActual?.toFixed(2) || '0';
      const dohertyRateTarget = result.dohertyRateTarget?.toFixed(2) || '0';

      return `Analysis of ${loadsText} page loads/work day at ${currentEPT}s EPT. Users can only work at a maximum of ${dohertyRateCurrent} transactions/minute at a load time of ${currentEPT}s, and a maximum of ${dohertyRateTarget} transactions/minute at a load time of ${targetEPT}s (Doherty Threshold research curve). Total impact: ${Math.round(totalAnnualImpact).toLocaleString()} hours/year lost. Linear impact: ${Math.round(result.annualLinearImpactHours || 0).toLocaleString()} hours (wait time). Exponential impact: ${Math.round(result.annualExponentialImpactHours || 0).toLocaleString()} hours (productivity slowdown from Doherty effect).`;
    } else {
      const totalAnnualImpact = (result.annualLinearImpactHours || 0) + (result.annualExponentialImpactHours || 0);
      const employeesText = result.employees > 1 ? `${result.employees} employees` : '1 employee';
      const currentEPT = result.ept.toFixed(1);

      return `${employeesText} performing ${result.expectedTasks} tasks/day (${result.clicksPerTask} clicks/task) at ${currentEPT}s EPT. Improving to 1.0s EPT could recover ${Math.round(totalAnnualImpact).toLocaleString()} hours/year: ${Math.round(result.annualLinearImpactHours || 0).toLocaleString()} hours from reduced wait time, ${Math.round(result.annualExponentialImpactHours || 0).toLocaleString()} hours from improved productivity (Doherty effect).`;
    }
  };

  return (
    <div className="card p-8" ref={analysisRef}>
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Analysis Summary</h4>
        <p className="text-sm text-blue-800 leading-relaxed">
          {generateSummary()}
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        <div className="flex-1 space-y-6">
          <div>
            <h3 className="text-2xl text-sf-blue-700 flex items-center gap-2">
              {result.knownExperiencesMode ? 'Known Experiences Analysis' : 'Productivity Analysis'}
              <div className="flex items-center gap-1">
                {result.isHoursMode ? <Clock className="w-5 h-5" /> : <DollarSign className="w-5 h-5" />}
                <UserCheck className={`w-5 h-5 ${result.isFullTime ? 'text-sf-blue-500' : 'text-gray-400'}`} />
              </div>
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Analysis Date: {new Date(result.calculationDate).toLocaleDateString()}
            </p>
            <p className="text-4xl font-bold text-sf-blue-500 mt-2">
              {result.knownExperiencesMode && result.theoreticalProductivityPercentage !== undefined 
                ? result.theoreticalProductivityPercentage.toFixed(1) 
                : result.productivity.toFixed(1)}%
            </p>
            <p className="text-sf-blue-600">
              {result.knownExperiencesMode ? 'theoretical productivity based on EPT' : 'of achievable productivity'}
            </p>
            {!result.knownExperiencesMode && (
              <p className="text-sf-blue-600">
                Experience Level: {result.experience} ({result.isFullTime ? 'Full-Time' : 'Part-Time'})
              </p>
            )}
            <div className="flex items-center gap-2 text-sf-blue-600">
              <Timer size={16} className="text-sf-blue-500" />
              <span>EPT: {result.ept.toFixed(1)}s</span>
              {result.knownExperiencesMode && result.targetEpt && (
                <span> → Target: {result.targetEpt.toFixed(1)}s</span>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500">
                {result.knownExperiencesMode ? 'Loads Per Work Day' : 'Tasks Per Day'}
              </p>
              <p className="text-lg font-semibold">
                {result.knownExperiencesMode ? result.tasksPerDay.toLocaleString() : `${result.tasksPerDay} / ${result.expectedTasks}`}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">
                {result.knownExperiencesMode ? 'Doherty Rate Capacity/Day' : 'Max Clicks/Day'}
              </p>
              <p className="text-lg font-semibold">{result.maxClicksPerDay.toFixed(0)}</p>
              <p className="text-xs text-gray-400">Doherty Rate @ {result.ept}s EPT</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">
                {result.knownExperiencesMode ? 'Theoretical Max Capacity/Day' : 'Theoretical Max Clicks/Day'}
              </p>
              <p className="text-lg font-semibold">{result.theoreticalMaxClicksPerDay.toFixed(0)}</p>
              <p className="text-xs text-gray-400">Theoretical Max @ {result.ept}s EPT</p>
            </div>
            {!result.knownExperiencesMode && (
              <div>
                <p className="text-sm font-medium text-gray-500">Expected Productivity</p>
                <p className="text-lg font-semibold">{result.expectedProductivity.toFixed(0)} clicks/day</p>
                <p className="text-xs text-gray-400">{result.clicksPerTask} × {result.tasksPerDay} tasks</p>
              </div>
            )}

            {result.knownExperiencesMode && (
              <>
                <div>
                  <p className="text-sm font-medium text-gray-500">Efficiency Ratio (Actual)</p>
                  <p className="text-lg font-semibold">{((result.efficiencyRatioActual || 0) * 100).toFixed(1)}%</p>
                  <p className="text-xs text-gray-400">Doherty vs Theoretical @ {result.ept}s</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Upside Progress Remaining (%)</p>
                  <p className="text-lg font-semibold">{(result.upsideProgressRemainingPercentage || 0).toFixed(1)}%</p>
                  <p className="text-xs text-gray-400">@ {result.ept}s EPT, progress remaining</p>
                </div>
                <div>
                  <Tooltip content="Average time it takes for a user to complete one productive interaction (load) at the current EPT">
                    <p className="text-sm font-medium text-gray-500">Time/Productive Load (Actual)</p>
                  </Tooltip>
                  <p className="text-lg font-semibold">{(result.secondsPerProductiveLoadActual || 0).toFixed(1)}s</p>
                </div>
                <div>
                  <Tooltip content="Average time it takes for a user to complete one productive interaction (load) at the target EPT">
                    <p className="text-sm font-medium text-gray-500">Time/Productive Load (Target)</p>
                  </Tooltip>
                  <p className="text-lg font-semibold">{(result.secondsPerProductiveLoadTarget || 0).toFixed(1)}s</p>
                </div>
              </>
            )}

            {!result.isHoursMode && (
              <>
                <div>
                  <Tooltip content="At the EPT submitted, this number represents the estimated dollar value of the net cost (or benefit) of an employee given their productivity as a percentage of the employer's total amount paid.">
                    <p className="text-sm font-medium text-gray-500">Est. Labor Cost</p>
                  </Tooltip>
                  <p className={`text-lg font-semibold ${result.trueCost >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(result.trueCost)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Combined Role Value</p>
                  <p className={`text-lg font-semibold ${result.totalRoleValue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(result.totalRoleValue)}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="md:w-3/5 space-y-6">
          <div className="h-[300px]">
            <h4 className="text-lg font-medium text-sf-blue-700 mb-4">
              {result.knownExperiencesMode && result.isHoursMode ? 'Annual Impact Visualization' : 'Impact Visualization'}
            </h4>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 80, right: 20, top: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  tickFormatter={(value) => result.isHoursMode ? formatHours(value) : formatCurrency(value)} 
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  width={80}
                  tick={{ 
                    fontSize: 12,
                    fontWeight: 600,
                    fill: '#374151',
                    dy: 0
                  }}
                />
                <RechartsTooltip 
                  formatter={(value: number) => [
                    result.isHoursMode ? formatHours(value) : formatCurrency(value),
                    ''
                  ]}
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb' }}
                />
                <Bar 
                  dataKey="value" 
                  fill="#3B82F6"
                  radius={[0, 4, 4, 0]}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {result.isHoursMode && (
            <div className="pt-4 border-t space-y-4">
              {result.knownExperiencesMode ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Total Company Load Time (Actual)</h5>
                      <p className="text-lg font-bold text-orange-600">{Math.round(result.actualTotalExposureSecondsPerDay || 0)}s</p>
                      <p className="text-xs text-gray-500">per day @ {result.ept}s EPT</p>
                    </div>
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Total Company Load Time (Target)</h5>
                      <p className="text-lg font-bold text-green-600">{Math.round(result.targetTotalExposureSecondsPerDay || 0)}s</p>
                      <p className="text-xs text-gray-500">per day @ {result.targetEpt}s EPT</p>
                    </div>
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Total Company Load Time Waste</h5>
                      <p className="text-lg font-bold text-red-600">{Math.round(result.wasteTotalSecondsPerDay || 0)}s</p>
                      <p className="text-xs text-gray-500">wasted per day company-wide</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Linear Annual Impact</h5>
                      <p className="text-2xl font-bold text-blue-600">
                        {Math.round(Math.abs(result.annualLinearImpactHours || 0)).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">hours/year vs {result.targetEpt}s EPT</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Exponential Annual Impact</h5>
                      <p className="text-2xl font-bold text-red-600">
                        {Math.round(Math.abs(result.annualExponentialImpactHours || 0)).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">hours/year vs {result.targetEpt}s EPT</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Linear Benefit of Improvement</h5>
                    <p className="text-xl font-bold text-blue-600">
                      {Math.round(result.annualLinearImpactHours || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">hours/year, Cross-Company</p>
                  </div>
                  
                  <div className="text-center">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Exponential Potential Benefit</h5>
                    <p className="text-xl font-bold text-green-600">
                      {Math.round(result.annualExponentialImpactHours || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">hours/year, Cross-Company</p>
                  </div>
                  
                  <div className="text-center">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">
                      {result.knownExperiencesMode ? 'Total Company Impact' : 'Per User Impact'}
                    </h5>
                    <p className={`text-xl font-bold ${result.hoursDifference && result.hoursDifference < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {result.hoursDifference?.toFixed(2) || '0.00'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {result.knownExperiencesMode ? 'hours/day company-wide' : 'hours/day'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-8 pt-6 border-t flex justify-center gap-4 flex-wrap export-exclude">
        <button
          onClick={handleEditClick}
          className="btn-secondary"
        >
          <Edit2 size={16} className="mr-2" />
          Edit Calculation
        </button>
        {onShowTrend && (
          <button
            onClick={onShowTrend}
            className="btn-primary"
          >
            Show Trend
          </button>
        )}
        <button
          onClick={() => handleExport('pdf')}
          className="btn-secondary"
        >
          <Download size={16} className="mr-2" />
          Export PDF
        </button>
        <button
          onClick={() => handleExport('png')}
          className="btn-secondary"
        >
          <Download size={16} className="mr-2" />
          Export PNG
        </button>
      </div>
    </div>
  );
}